import { Injectable, inject } from '@angular/core';
import { FIRESTORE_TOKEN } from '../firebase/firestore.provider';
import { FIRESTORE_OPS_TOKEN } from '../firebase/firestore-operations.provider';
import { Observable, from, map } from 'rxjs';
import { AccessLog } from '../../shared/interfaces';
import { formatDate } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class AccessLogService {
  private firestore = inject(FIRESTORE_TOKEN);
  private ops = inject(FIRESTORE_OPS_TOKEN);
  private collectionName = 'access_logs';

  /**
   * 指定された期間のアクセスログを取得する
   * @param from 開始日時
   * @param to 終了日時
   * @returns 取得したアクセスログの配列
   */
  fetchAccessLogs(fromDate: Date, toDate: Date): Observable<AccessLog[]> {
    const fromTimestamp = this.ops.Timestamp.fromDate(fromDate);
    // 終了日はその日の23:59:59までを含めるため、翌日の00:00:00未満でフィルタするか、引数で渡された値をそのまま使うか
    // ここでは単純に引数の Date を Timestamp に変換して where で挟む形とする。
    const toTimestamp = this.ops.Timestamp.fromDate(toDate);

    const logsRef = this.ops.collection(this.firestore, this.collectionName);
    const q = this.ops.query(
      logsRef,
      this.ops.where('createdAt', '>=', fromTimestamp),
      this.ops.where('createdAt', '<=', toTimestamp),
      this.ops.orderBy('createdAt', 'desc')
    );

    return from(this.ops.getDocs(q)).pipe(
      map(snapshot => {
        return snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }) as AccessLog);
      })
    );
  }

  /**
   * CSVエクスポート用のヘッダーを取得する
   */
  getHeaders(): string[] {
    return [
      '日時',
      'ユーザーID',
      'ユーザー名',
      'アクション',
      'リソース',
      'IPアドレス',
      'ユーザーエージェント',
      'ステータス'
    ];
  }

  /**
   * アクセスログの配列をCSVエクスポート用の行データに変換する
   * @param logs アクセスログ配列
   */
  mapToExportRows(logs: AccessLog[]): string[][] {
    return logs.map(log => [
      log.createdAt ? formatDate(log.createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss', 'en-US') : '',
      log.userId || '',
      log.userName || '',
      log.action || '',
      log.resource || '',
      log.ipAddress || '',
      log.userAgent || '',
      log.status || ''
    ]);
  }

  /**
   * アクセスログを追加する
   * @param entry 追加するアクセスログデータ
   */
  async addLog(entry: Omit<AccessLog, 'id' | 'createdAt'>): Promise<void> {
    try {
      const logsRef = this.ops.collection(this.firestore, this.collectionName);
      await this.ops.addDoc(logsRef, {
        ...entry,
        createdAt: this.ops.serverTimestamp()
      });
    } catch (err) {
      console.error('アクセスログの記録に失敗しました', err);
    }
  }
}
